<script setup lang="ts">
import { computed } from 'vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import { Users, Crown, MessageCircle } from 'lucide-vue-next'

interface Participant {
  id: number
  name: string
  avatar_url: string | null
  role: string
}

const props = defineProps<{
  participants: Participant[]
  subject?: { id: number; name: string } | null
  level?: { id: number; name: string } | null
  conversationType?: string
}>()

const isDirect = computed(() => props.conversationType === 'direct')
const headerTitle = computed(() => isDirect.value ? 'Conversation' : 'Members')

const getInitials = (name: string): string => {
  return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().substring(0, 2)
}
</script>

<template>
  <div class="w-64 border-l border-gray-200 bg-white flex flex-col shrink-0">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 shrink-0">
      <h3 class="text-sm font-semibold text-gray-900">{{ headerTitle }}</h3>
    </div>

    <!-- Subject/Level info (group only) -->
    <div v-if="!isDirect && (subject || level)" class="px-4 py-2 border-b border-gray-100">
      <p v-if="subject" class="text-xs text-gray-500">
        <span class="font-medium text-gray-700">{{ subject.name }}</span>
      </p>
      <p v-if="level" class="text-xs text-gray-400">{{ level.name }}</p>
    </div>

    <!-- Direct conversation info -->
    <div v-if="isDirect" class="px-4 py-2 border-b border-gray-100">
      <div class="flex items-center gap-1.5 text-xs text-gray-500">
        <MessageCircle class="w-3.5 h-3.5" />
        <span>Direct message</span>
      </div>
    </div>

    <!-- Participant count (group only) -->
    <div v-if="!isDirect" class="px-4 py-2 border-b border-gray-100">
      <div class="flex items-center gap-1.5 text-xs text-gray-500">
        <Users class="w-3.5 h-3.5" />
        <span>{{ participants.length }} member{{ participants.length !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Participant list -->
    <div class="flex-1 overflow-y-auto scrollbar-hover">
      <div
        v-for="participant in participants"
        :key="`${participant.id}-${participant.role}`"
        class="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50"
      >
        <Avatar class="w-8 h-8 shrink-0">
          <AvatarImage :src="participant.avatar_url" />
          <AvatarFallback class="text-xs" :class="participant.role === 'teacher' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'">
            {{ getInitials(participant.name) }}
          </AvatarFallback>
        </Avatar>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-gray-900 truncate">{{ participant.name }}</p>
          <p v-if="participant.role === 'teacher'" class="text-[10px] text-blue-600 flex items-center gap-0.5">
            <Crown class="w-3 h-3" />
            Tutor
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
